/* eslint-disable @stylistic/max-len */
import React, {type RefObject} from 'react';

/**
 * The `CrossProps` interface defines the shape of the properties object that is expected for this component.
 * It outlines the required properties that needs to be provided when utilizing this component expecting an object of this type.
 */
interface CrossProps {
    /**
     * A string representing the CSS class to be applied to the CrossIcon element to override its default styling.
     *
     * @default ''
     */
    className?: string;
    /**
     * A string representing the color to be applied.
     *
     * @default '#0050FF'
     */
    color1?: string;
    /**
     * A number or string representing the height of the CrossIcon element.
     *
     * @default 24
     */
    height?: number | string;
    /**
     * The `ref` property represents a reference to the SVG element, allowing for direct manipulation or access to the DOM node.
     */
    ref?: RefObject<SVGSVGElement | null>;
    /**
     * The `testId` property represents a unique identifier, usually in the form of a string, assigned to a component for testing purposes.
     * This property is crucial for uniquely identifying components during testing, allowing for more accurate and reliable tests.
     *
     * @default 'Cross'
     */
    testId?: string;
    /**
     * A number or string representing the height of the CrossIcon element.
     *
     * @default 24
     */
    width?: number | string;
}

/**
 * The `Cross` component is a functional component designed to render a customizable SVG element representing an Icon.
 * It receives several props through the `CrossProps` interface, allowing for customization of class, color, dimensions, and testing identifier.
 *
 * @param props           The component props.
 * @param props.className A string representing the CSS class to be applied to the CrossIcon element to override its default styling.
 * @param props.color1    A string representing the color to be applied to the Icon.
 * @param props.height    A number or string representing the height of the CrossIcon element.
 * @param props.ref       A reference to the SVG element, allowing for direct manipulation or access to the DOM node.
 * @param props.testId    A string representing a unique identifier assigned to the component for testing purposes.
 * @param props.width     A number or string representing the height of the CrossIcon element.
 * @returns A React element representing the `Check` component with the specified properties.
 *
 * @example
 * ```tsx
 * const CrossComponent = <Cross className="myClass" color1="#000" height="20" testId="myTestId" width="20" />;
 * ```
 */
export const Cross = ({
    className = '',
    color1 = '#0050FF',
    height = 24,
    ref,
    testId = 'Cross',
    width = 24
}: CrossProps) => (
    <svg
        ref={ref}
        className={className}
        data-cy={testId}
        height={height}
        viewBox="0 0 24 24"
        width={width}
        xmlns="http://www.w3.org/2000/svg"
        xmlnsXlink="http://www.w3.org/1999/xlink"
    >
        <g fill={color1}>
            <path d="M17.02 6.98a.7.7 0 0 0-1.04 0L12 10.95 8.03 6.98a.73.73 0 0 0-1.05 0c-.3.3-.3.74 0 1.05L10.95 12l-3.97 3.97c-.3.3-.3.76 0 1.05q.22.23.52.23t.53-.23L12 13.05l3.97 3.97q.24.23.53.23t.52-.23c.3-.3.3-.75 0-1.04L13.05 12l3.97-3.97c.3-.3.3-.75 0-1.05" />
            <path d="M12 1.5C6.23 1.5 1.5 6.22 1.5 12c0 5.77 4.72 10.5 10.5 10.5 5.77 0 10.5-4.73 10.5-10.5S17.77 1.5 12 1.5M12 21c-4.95 0-9-4.05-9-9s4.05-9 9-9 9 4.05 9 9-4.05 9-9 9" />
        </g>
    </svg>
);

Cross.displayName = 'Cross';